import { useMemo } from 'react'
import type { CSSProperties } from 'react'
import { useSales } from '../../hooks/useSales'
import { useExpenses } from '../../hooks/useExpenses'
import { usePrints } from '../../hooks/usePrints'
import { buildEventList, groupEventsByDay } from '../../lib/timeline'
import { Card, DayBlock, EventRow } from '../../components/ui'
import { type TimeRange, timeRangeLabel, isWithinRange } from '../../lib/timeRange'

// Cap the visible day groups even on long ranges so the DOM stays small.
const MAX_DAY_GROUPS = 60

interface DailyTimelineProps {
  range: TimeRange
}

function formatDayLabel(isoDate: string): string {
  // YYYY-MM-DD → M/D
  const [, m, d] = isoDate.split('-').map(Number)
  return `${m}/${d}`
}

export default function DailyTimeline({ range }: DailyTimelineProps) {
  const { data: sales } = useSales()
  const { data: expenses } = useExpenses()
  const { data: prints } = usePrints()

  const dayGroups = useMemo(() => {
    if (!sales || !expenses || !prints) return []
    const rangeSales = sales.filter((s) => isWithinRange(s.sale_date, range))
    const rangeExpenses = expenses.filter((e) => isWithinRange(e.expense_date, range))
    const rangePrints = prints.filter((p) => isWithinRange(p.print_date, range))

    const events = buildEventList({
      sales: rangeSales.map((s) => ({
        id: s.id,
        sale_date: s.sale_date,
        gross_amount: Number(s.gross_amount),
        platform: s.platform,
        customer_name: s.customer_name,
      })),
      expenses: rangeExpenses.map((e) => ({
        id: e.id,
        expense_date: e.expense_date,
        amount: Number(e.amount),
        description: e.description,
        category: e.category,
        linked_spool_id: e.linked_spool_id,
      })),
      prints: rangePrints.map((p) => ({
        id: p.id,
        print_date: p.print_date,
        description: p.description,
        purpose: p.purpose,
        total_cost: Number(p.total_cost),
        units_produced: p.units_produced,
      })),
    })
    return groupEventsByDay(events).slice(0, MAX_DAY_GROUPS)
  }, [sales, expenses, prints, range])

  if (sales === undefined || expenses === undefined || prints === undefined) return null

  if (dayGroups.length === 0) {
    return (
      <Card>
        <div style={emptyStyle}>No activity in the last {timeRangeLabel(range)}.</div>
      </Card>
    )
  }

  return (
    <Card padding="var(--space-3)">
      <div style={labelStyle}>Recent activity · last {timeRangeLabel(range)}</div>
      <div
        style={{
          maxHeight: '60vh',
          overflowY: 'auto',
          paddingRight: 'var(--space-2)',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {dayGroups.map((g) => (
          <DayBlock key={g.date} date={formatDayLabel(g.date)} summary={g.summary}>
            {g.events.map((e) => (
              <EventRow key={e.id} kind={e.kind} title={e.title} amount={e.amount} />
            ))}
          </DayBlock>
        ))}
      </div>
    </Card>
  )
}

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-faint)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 600,
  padding: 'var(--space-2) var(--space-3)',
  marginBottom: 'var(--space-1)',
}

const emptyStyle: CSSProperties = {
  textAlign: 'center',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-sm)',
  padding: 'var(--space-4)',
}
